import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { TextField } from '@mui/material';
import _ from 'lodash';

import {
    ActionButton,
    DEFAULT_OPEN_LOCATION_ACTION,
    getDefaultButton,
} from '../../model/components/ActionButton';
import { PopTextV2 } from '../../model/components/PopTextv2';
import { EventsCalendarPostData } from '../../model/types/EventsCalendarPostData';
import { PathContextProvider } from '../../PathContext';
import { ACTION_OPTIONS, ActionButtonInput } from './ActionButtonInput';
import { ArrayField, ArrayFieldItem } from './ArrayField';
import { FileInput } from './FileInput';
import { PopTextV2Input } from './PopTextV2Input';
import {
    EMPTY_TRANSLATION_TABLE,
    EventsCalendarVariant,
    hexToUnityColor,
} from '@lifeserver/shared';

import styles from './EventCalendarPostInput.module.scss';

const DEFAULT_POP_TEXT_V2: PopTextV2 = {
    position: { x: 0, y: 0 },
    animated: true,
    backgroundColor: hexToUnityColor('#000000'),
    textColor: hexToUnityColor('#FFFFFF'),
    text: EMPTY_TRANSLATION_TABLE,
    fontSize: 60,
};

// The two editable states of an Events Calendar entry, mapped to positions
// in the post's `variants` list. The wire contract is a flexible, ordered
// `variants[]` (the client picks one by a numeric `activeFrom` threshold);
// the CMS exposes the common two-state case as labelled tabs. The baseline
// variant (index 0) is the teaser shown before the event starts; the active
// variant (index 1) is shown once the event has started.
const VARIANTS = [
    {
        index: 0,
        label: 'Scheduled',
        tooltipId: 'eventsCalendar-scheduled-info',
        tooltipContent:
            'Scheduled state refers to upcoming posts, these are activities players cannot yet participate in',
    },
    {
        index: 1,
        label: 'Active',
        tooltipId: 'eventsCalendar-active-info',
        tooltipContent:
            'Active state refers to active posts, these are ongoing activities players can participate in',
    },
] as const satisfies ReadonlyArray<{
    index: number;
    label: string;
    tooltipId: string;
    tooltipContent: string;
}>;
type VariantIndex = (typeof VARIANTS)[number]['index'];

export interface EventCalendarPostInputProps {
    value: EventsCalendarPostData;
    onChange?: (value: EventsCalendarPostData) => void;
}

export const EventCalendarPostInput = ({
    value,
    onChange,
}: EventCalendarPostInputProps) => {
    const [activeVariantIndex, setActiveVariantIndex] =
        useState<VariantIndex>(0);

    // Normalize legacy payloads created before the `variants` field existed.
    const variants: EventsCalendarVariant[] = value.variants ?? [
        {
            imageUrl: '',
            button: getDefaultButton(DEFAULT_OPEN_LOCATION_ACTION),
        },
        { imageUrl: '' },
    ];
    useEffect(() => {
        if (!value.variants) {
            onChange?.({ ...value, variants });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onFieldChange = <Field extends keyof EventsCalendarPostData>(
        field: Field,
        fieldValue: EventsCalendarPostData[Field],
    ) => {
        onChange?.({
            ...value,
            [field]: fieldValue,
        });
    };

    const onVariantFieldChange = <Field extends keyof EventsCalendarVariant>(
        index: number,
        field: Field,
        fieldValue: EventsCalendarVariant[Field],
    ) => {
        // We intentionally do NOT mirror anything between variants. The CMS
        // only edits the button on the baseline variant (the input is hidden
        // on the others); Unity sources the action button from the baseline
        // variant and renders it once the event is active. Keeping later
        // variants sparse avoids shipping duplicate data.
        const updatedVariants = (value.variants ?? variants).map(
            (variant, i) =>
                i === index ? { ...variant, [field]: fieldValue } : variant,
        );
        onChange?.({
            ...value,
            variants: updatedVariants,
        });
    };

    const activeVariant = variants[activeVariantIndex];

    return (
        <div className={styles.container}>
            <fieldset style={{ display: 'none' }}>
                <legend>Entry Type</legend>
                <TextField
                    fullWidth
                    value={value.entryType}
                    onChange={(e) => onFieldChange('entryType', e.target.value)}
                    placeholder="e.g., CTA, Info, Promo"
                />
            </fieldset>

            <div className={styles.tabs}>
                {VARIANTS.map(({ index, label, tooltipId, tooltipContent }) => (
                    <button
                        key={index}
                        type="button"
                        className={`${styles.tab} ${
                            activeVariantIndex === index ? styles.activeTab : ''
                        }`}
                        onClick={() => setActiveVariantIndex(index)}
                    >
                        {label}
                        <span
                            className={styles.infoIcon}
                            data-tooltip-id={tooltipId}
                            data-tooltip-content={tooltipContent}
                            // Stop the tab from toggling when the user
                            // clicks the icon to inspect the tooltip.
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`${label} state info`}
                            role="img"
                        >
                            ⓘ
                        </span>
                    </button>
                ))}
            </div>
            {VARIANTS.map(({ tooltipId }) => (
                <Tooltip
                    key={tooltipId}
                    id={tooltipId}
                    place="bottom"
                    delayShow={300}
                    className={styles.tooltip}
                />
            ))}

            <PathContextProvider segment="variants">
                <PathContextProvider segment={activeVariantIndex}>
                    <EventsCalendarVariantInput
                        value={activeVariant}
                        showButton={activeVariantIndex === 0}
                        onImageUrlChange={(imageUrl) =>
                            onVariantFieldChange(
                                activeVariantIndex,
                                'imageUrl',
                                imageUrl,
                            )
                        }
                        onButtonChange={(button) =>
                            onVariantFieldChange(
                                activeVariantIndex,
                                'button',
                                button,
                            )
                        }
                        onPopTextV2sChange={(popTextV2s) =>
                            onVariantFieldChange(
                                activeVariantIndex,
                                'popTextV2s',
                                popTextV2s,
                            )
                        }
                    />
                </PathContextProvider>
            </PathContextProvider>
        </div>
    );
};

interface EventsCalendarVariantInputProps {
    value: EventsCalendarVariant;
    showButton: boolean;
    onImageUrlChange: (imageUrl: string) => void;
    onButtonChange: (button: ActionButton) => void;
    onPopTextV2sChange: (popTextV2s: PopTextV2[]) => void;
}

const EventsCalendarVariantInput = ({
    value,
    showButton,
    onImageUrlChange,
    onButtonChange,
    onPopTextV2sChange,
}: EventsCalendarVariantInputProps) => {
    return (
        <>
            <fieldset>
                <legend>Image</legend>
                <FileInput
                    allowedFileTypes="image"
                    value={
                        value.imageUrl
                            ? { url: value.imageUrl, fileType: 'image' }
                            : undefined
                    }
                    onChange={(file) => onImageUrlChange(file?.url ?? '')}
                />
            </fieldset>

            {showButton && value.button && (
                <fieldset>
                    <legend>Button</legend>
                    <PathContextProvider segment="button">
                        <ActionButtonInput
                            value={value.button}
                            allowedActionTypes={ACTION_OPTIONS}
                            onChange={onButtonChange}
                        />
                    </PathContextProvider>
                </fieldset>
            )}

            <fieldset>
                <legend>Pop Texts</legend>
                <PathContextProvider segment="popTextV2s">
                    <ArrayField
                        items={value.popTextV2s ?? []}
                        onChange={onPopTextV2sChange}
                        onAdd={() => {
                            const newPopTextV2s = value.popTextV2s
                                ? [
                                      ...value.popTextV2s,
                                      _.cloneDeep(DEFAULT_POP_TEXT_V2),
                                  ]
                                : [_.cloneDeep(DEFAULT_POP_TEXT_V2)];
                            onPopTextV2sChange(newPopTextV2s);
                        }}
                    >
                        {(element) => (
                            <ArrayFieldItem key={element.index} {...element}>
                                <PathContextProvider segment={element.index}>
                                    <PopTextV2Input
                                        value={element.item}
                                        onChange={element.onChange}
                                    />
                                </PathContextProvider>
                            </ArrayFieldItem>
                        )}
                    </ArrayField>
                </PathContextProvider>
            </fieldset>
        </>
    );
};
